import { FormikProvider, getIn, useFormikContext } from "formik";
import ComboBoxFormik from "../../../../../../../common/components/form/ComboBoxFormik";
import { AvailableAdditionsFieldFormik } from "../../../../forms/fields/AvailableAdditionsFieldFormik";
import ButtonWithLoader from "../../../../../../../common/components/ui/loading/ButtonLoading";
import { IRenter, SignatureInfo } from "../../../../../../../types/renter.type";
import { useMemo, useState } from "react";
import { createContract } from "../../../../../services/integration.api";
import ZapSignActivate from "../../UploadInfoApis/ZapSignActivate";
import ZapSignConfirmation from "../../UploadInfoApis/ZapSignConfirmation";

import { additionsPlanRenter } from "../../../../../../../common/services/renters.api";
import { useToastContext } from "../../../../../../../common/components/ui/toast/toast.context";
import { useCompanyStore } from "../../../../../../../common/states/store/Company";
import {
  getConsultantOptions,
  getConsultantOptionsPlus,
} from "../../../../../../../common/helpers/technicalConsultant";

type Props = {
  data: IRenter;
};

type Period = {
  value: string;
  label: string;
  months: number;
  days?: number;
};
//eslint-disable-next-line react-refresh/only-export-components
export function getPlanLabel(planType: string): string {
  const planTypeMapping: { [key: string]: string } = {
    monthly: "MENSAL",
    quarterly: "TRIMESTRAL",
    yearly: "ANUAL",
    biennale: "BIENAL",
    triennial: "TRIENAL",
    thirtymonths: "30 MESES",
    thirtyfourmonths: "34 MESES",
  };

  return planTypeMapping[planType] || "DESCONHECIDO";
}

//eslint-disable-next-line react-refresh/only-export-components
export function getPlanMonths(planType: string): number {
  const planTypeMonthsMapping: { [key: string]: number } = {
    monthly: 1,
    quarterly: 3,
    yearly: 12,
    biennale: 24,
  };

  return planTypeMonthsMapping[planType] || 0; // Retorna 0 se o tipo de plano for desconhecido
}

//eslint-disable-next-line react-refresh/only-export-components
export function getPlanDays(planType: string): number {
  const planTypeDaysMapping: { [key: string]: number } = {
    monthly: 30, // Supõe-se que cada mês tem 30 dias
    quarterly: 90, // 3 meses
    yearly: 365, // Pode usar 365 se quiser contar todos os dias
    biennale: 730, // 2 anos
  };

  return planTypeDaysMapping[planType] || 0; // Retorna 0 se o tipo de plano for desconhecido
}

export default function AdditionsPlan(props: Props) {
  const renter = props.data;
  const formik = useFormikContext();
  const { notifySuccess, notifyError } = useToastContext();
  const [bindChangePlan, setBindChangePlan] = useState(false);
  const [signatureInfo, setSignatureInfo] = useState<SignatureInfo>();
  const [complement, setComplement] = useState("");
  const periodRenter = renter.activationData?.plan.planType;
  const reason = getIn(formik.values, "reason");
  const renterId = getIn(formik.values, "renterId");
  const vehicleId = getIn(formik.values, "vehicle._id");
  const additions = getIn(formik.values, "additions");
  const technicalConsultant = getIn(formik.values, "technicalConsultant");
  const { filterMode, selectedCompanies } = useCompanyStore();
  const singleSelectedCompany = useMemo(() => {
    if (filterMode === "cities" && selectedCompanies.length === 1) {
      return selectedCompanies[0];
    }
    return null; // Nenhuma empresa única ou seleção diferente
  }, [filterMode, selectedCompanies]);
  function handleSignature(contractInfo) {
    setSignatureInfo(contractInfo);
  }
  const period: Period = {
    value: renter.activationData?.plan?.planType,
    label: getPlanLabel(renter.activationData?.plan?.planType),
    months: getPlanMonths(renter.activationData?.plan?.planType),
    days: getPlanDays(renter.activationData?.plan?.planType),
  };

  const contractData = {
    renterId: renterId,
    vehicleId: vehicleId,
    period: period,
    additions: additions,
  };

  async function handlerChangePlan() {
    try {
      setBindChangePlan(true);
      const changePlanData = {
        renterId: renterId,
        vehicleId: vehicleId,
        periodRenter: periodRenter,
        reason: reason,
        returnReason: complement,
        additions: additions,
        technicalConsultant: technicalConsultant, // Verifique se este valor está correto
      };

      const result = await additionsPlanRenter(changePlanData);

      if (!result.ok) {
        notifyError(
          (result.message as string) || "Erro ao alterar o plano do cliente"
        );
        return;
      }

      notifySuccess("Plano do cliente alterado com sucesso");
    } catch (err) {
      console.log(err);
      notifyError("Ocorreu um erro ao processar a solicitação");
    } finally {
      setBindChangePlan(false);
    }
  }
  const consultantOptions = useMemo(() => {
    const baseOptions = getConsultantOptions(
      singleSelectedCompany?.companyName || ""
    );
    const plusOptions = getConsultantOptionsPlus();
    return [
      { value: "", label: "Selecione..." },
      ...baseOptions.filter((opt) => opt.value !== ""),
      ...plusOptions.filter((opt) => opt.value !== ""),
    ];
  }, [singleSelectedCompany]);
  const consultantOptionsPlus = getConsultantOptionsPlus();

  // ADICIOAR A consultantOptions AS OPCÕES DE TechnicalConsultantPlus
  consultantOptions.push(...consultantOptionsPlus);

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <ComboBoxFormik
          name="reason"
          label="Tipe de alteração"
          options={[
            { value: "", label: "-" },
            { value: "ADD", label: "Adicionar Serviços" }, // Chave do enum
            { value: "REMOVE", label: "Remover Serviços" }, // Chave do enum
          ]}
          required={true}
        />
        <div className="flex flex-col gap-1">
          <label
            className="block text-sm font-medium text-gray-600"
            htmlFor="otherReason "
          >
            Complemento
          </label>
          <textarea
            id="otherReason"
            name="otherReason"
            value={complement}
            onChange={(e) => setComplement(e.target.value)}
            style={{ resize: "none", height: "90px" }}
          />
        </div>

        <div>
          <h2>Escolha os aditivos:</h2>
          <AvailableAdditionsFieldFormik
            initialValues={renter.activationData?.plan.additions.map(
              (addition) => ({
                label: addition.label,
                value: parseFloat(addition.value), // Converte o valor de string para number
                key: addition.key,
              })
            )}
          />
        </div>

        <ComboBoxFormik
          label="Consultor:"
          name="technicalConsultant"
          options={consultantOptions}
          required={true}
        />

        <div className="flex justify-end mb-6">
          <ButtonWithLoader
            type="button"
            isLoading={bindChangePlan}
            onClick={handlerChangePlan}
          >
            Salvar Altereção
          </ButtonWithLoader>
        </div>
      </FormikProvider>

      <div className="mt-4">
        <ZapSignActivate
          onSave={function (): void {
            throw new Error("Function not implemented.");
          }}
          contractData={contractData}
          handleSignature={handleSignature}
          contractFn={createContract}
        />
        {signatureInfo?.ok && (
          <ZapSignConfirmation contractInfo={signatureInfo} />
        )}
      </div>
    </form>
  );
}
